/**
 * iOS standalone fix. An installed iOS PWA (`black-translucent` status bar, `viewport-fit=cover`) can
 * lay the page out one status-bar height short: `position: fixed; inset: 0`, `100%` and `100vh` all end
 * above the bottom edge and leave a dark band. `screen.height` is the real full height there, so the
 * shell takes the larger of the two. Everywhere else (browsers, desktop PWAs) this does nothing.
 */
export interface HeightInput {
  innerHeight: number;
  screenWidth: number;
  screenHeight: number;
  /** `navigator.standalone`: an iOS-only flag, true in the installed app. */
  iosStandalone: boolean;
  portrait: boolean;
}

/** The height in px the app shell must have, or `null` to leave sizing to the browser. */
export function appHeightPx(i: HeightInput): number | null {
  if (!i.iosStandalone) return null;
  // iOS keeps screen.width/height in portrait terms; pick the side that matches the orientation
  const long = Math.max(i.screenWidth, i.screenHeight);
  const short = Math.min(i.screenWidth, i.screenHeight);
  const screen = i.portrait ? long : short;
  return Math.max(i.innerHeight, screen);
}

/** Keeps `--app-h` on <html> in step with the screen; returns the cleanup. */
export function installAppHeight(): () => void {
  const root = document.documentElement;
  const apply = () => {
    const h = appHeightPx({
      innerHeight: window.innerHeight,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      iosStandalone: (navigator as Navigator & { standalone?: boolean }).standalone === true,
      portrait: window.innerHeight >= window.innerWidth,
    });
    const before = root.style.getPropertyValue("--app-h");
    if (h === null) root.style.removeProperty("--app-h");
    else root.style.setProperty("--app-h", `${h}px`);
    // Leaflet and the 3D canvas size themselves on window resize
    if (before !== (h === null ? "" : `${h}px`)) window.dispatchEvent(new Event("resize"));
  };
  apply();
  window.addEventListener("resize", apply);
  window.addEventListener("orientationchange", apply);
  window.addEventListener("pageshow", apply);
  return () => {
    window.removeEventListener("resize", apply);
    window.removeEventListener("orientationchange", apply);
    window.removeEventListener("pageshow", apply);
  };
}
